import PatientSignatureImage from './PatientSignatureImage';
import AnesthesiaSelector from './AnesthesiaSelector';

const SegAuthorizationForm = ({
    pageRefs,
    currentPage,
    logo,
    pageTitles,
    SurgeryFormData,
    surgeonOptionsMap,
    docSignature,
    setSignatures,
    setSurgeryFormData,
    handleInputChange,
    invalidFields,
    isNext,
    isEditing,
    signatures,
    canvasRefs,
    updateSignatureData,
    clearSignature,
    toggleEditMode,
    getCentralAmericaDate,
    getCentralAmericaTime,
    Logo,
    Head,
    Paragraph,
    RadioGroup,
    TextInput,
    SignatureField,
    SegLogoBanner,
    Initials,
    surgeryUpload,
    FormInput,
    anesthesiaOptions,
    formattedDate,
    LeftCheckBox,
    handleCheckboxChange,
    eye,
    SegFooter
}) => {

    return (
        <div ref={pageRefs.current[currentPage]} className="max-w-5xl w-full bg-white">
            <SegLogoBanner src={logo} />
            <div className="px-1 mx-8 py-2">
                <Head title="Authorization for Medical Procedures/Surgery" />
                <Paragraph>
                    Patient Name: <span className="underline font-bold">{SurgeryFormData.first_name} {SurgeryFormData.last_name}</span>  DOB: <span className="underline font-bold">{SurgeryFormData.dob}</span>
                </Paragraph>
                <div className="flex items-center justify-center w-full">
                    <Paragraph className="text-center">
                        <span className={eye === "os" ? "line-through font-bold" : "font-bold"}>
                            RIGHT EYE
                        </span>
                        /
                        <span className={eye === "od" ? "line-through font-bold" : "font-bold"}>
                            LEFT EYE
                        </span>
                    </Paragraph>
                </div>
                <Paragraph>
                    1. I hereby authorize, {SurgeryFormData.surgeon} and/or such assistants as may be selected by him/her and the medical staff at the surgical facilities affiliated with Southern Eye Group to remedy the condition or conditions which appear indicated by the diagnostic studies already performed, or to perform diagnostic studies as deemed necessary.
                </Paragraph>
                <Paragraph>
                    2. The procedure(s) necessary to be performed has/have been explained to me by my doctor, and I understand the nature or the procedure to be:
                </Paragraph>


                {eye && (
                    <>
                        <LeftCheckBox
                            label=""
                            items={Object.keys(SurgeryFormData?.[`procedure_json_${eye}`] || {}).filter(item => item !== 'Other procedures')}
                            values={Object.fromEntries(
                                Object.entries(SurgeryFormData?.[`procedure_json_${eye}`] || {}).filter(([key]) => key !== 'Other procedures')
                            )}
                            onChange={(proc, checked) =>
                                handleCheckboxChange(`procedure_json_${eye}`, proc, checked)
                            }
                        />
                        <TextInput
                            label="Other Procedures"
                            name={`other_procedures_${eye}`}
                            value={SurgeryFormData?.[`procedure_json_${eye}`]?.['Other procedures'] || ''}
                            onChange={(e) =>
                                setSurgeryFormData({
                                    ...SurgeryFormData,
                                    [`procedure_json_${eye}`]: {
                                        ...SurgeryFormData?.[`procedure_json_${eye}`],
                                        'Other procedures': e.target.value,
                                    },
                                })
                            }
                            disabled={isNext}
                        />
                    </>
                )}
                <Paragraph>
                    3. It has been explained to me that during the operation/procedure, unforeseen conditions may be revealed that necessitate an extension of the original procedure(s) or different procedure(s) than those set forth in Paragraph 2. I therefore authorize and request that the above-named physician, their assistants, or their designees perform such medical and surgical procedures as are necessary and desirable in the exercise of professional judgement. The authority granted under this Paragraph 3 shall extend to remedying all conditions that require treatment and are not known to the doctor or the medical staff of the surgical facility at the time the operation is commenced.
                </Paragraph>
                <Paragraph>
                    4. I have been advised of alternative means of therapy and of risks and consequence associated with these procedures and of other risks such as loss of blood, infection, and cardiac arrest, which are attendant on the performance of any surgical or medical procedure. I am further aware that the practice of medicine and surgery is not an exact science and acknowledge that no guarantees have been made to me concerning the risk or results of this operation or procedure.
                </Paragraph>
                <Paragraph>5. I consent to the administration of anesthesia to be applied by a qualified member of the anesthesia staff approved by my physician/surgeon and the surgical facility affiliate and the use of anesthetics as may be deemed advisable, which would include one or more of the following: </Paragraph>

                <AnesthesiaSelector
                    label="Anesthesia"
                    name="anesthesia"
                    options={anesthesiaOptions}
                    selectedValues={Array.isArray(SurgeryFormData.anesthesia) ? SurgeryFormData.anesthesia : (SurgeryFormData.anesthesia ? [SurgeryFormData.anesthesia] : [])}
                    onChange={(values) =>
                        setSurgeryFormData({
                            ...SurgeryFormData,
                            anesthesia: values,
                        })
                    }
                    disabled={isNext}
                    required
                />

                <Paragraph>6. I consent to the taking and publication of any photographs in the course of this operation for the purpose
                    of advancing medical education and to the disposition of any tissues or parts removed at this operation, including their dissection for scientific or diagnostic purpose. </Paragraph>

                <Paragraph>7. I certify that I have read and fully understand the above information. Inapplicable paragraphs were
                    stricken before I have signed.
                </Paragraph>



                <div className="my-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <PatientSignatureImage
                        signature={SurgeryFormData['patient_signature']}
                        label="Patient/Guardian Signature"
                        signedDate={SurgeryFormData.patientSignedDate}
                    />

                    <div className="flex items-start gap-3 min-w-0">
                        <div className="flex-1 min-w-0">
                            <PatientSignatureImage
                                signature={SurgeryFormData['witness_signature']}
                                label="Witness Signature"
                                signedDate={SurgeryFormData.witnessSignedDate}
                            />
                        </div>

                        {(!isNext && !SurgeryFormData.witnessSignedDate) && (
                            <button
                                type="button"
                                onClick={() =>
                                    setSurgeryFormData({
                                        ...SurgeryFormData,
                                        witnessSignedDate: getCentralAmericaDate(),
                                        witnessSignedTime: getCentralAmericaTime(),
                                    })
                                }
                                disabled={isNext}
                                className="shrink-0 p-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white focus:outline-none"
                                title="Set Central America Date & Time"
                            >
                                <i className="fas fa-calendar-days text-white text-lg"></i>
                            </button>
                        )}
                    </div>
                </div>


                <PatientSignatureImage
                    signature={SurgeryFormData['doctor_signature']}
                    label="Physician/Surgeon"
                    signedDate={formattedDate}
                />

            </div>
            <div className="mb-2">
                <SegFooter />
            </div>
        </div>

    );
};

export default SegAuthorizationForm;